import { Badge, Button, Progress } from 'reactstrap';
import {
  usePauseBatchMutation,
  useResumeBatchMutation,
  useAbortBatchMutation,
  BatchProgress,
  BatchSummary,
  BatchStatus,
  StatusCounts,
  ProcessingStats,
  DocumentMetadata,
} from '../../../api/ai-supervisor';
import { useContext, useMemo } from 'react';
import { SurveyContext, SurveyContextLoadedProps } from '../../survey-container/SurveyContainer';
import { DashboardSection } from '../../../molecules/dashboard';
import { QuestionState } from '../hooks/useBatchSSE';
import { BasicAlert } from '../../../molecules/alert';
import { DATE, formatDateNonUTC, getDiff, getDuration } from '../../../utils/date';
import { generateErrorToast, generateToast } from '../../../molecules/toasts';
import { getAIModelName } from '../../../api/ai';

interface InfoHeaderProps {
  batchId: string;
  sseData: {
    connected: boolean;
    status: BatchStatus | null;
    progress: BatchProgress | null;
    summary: BatchSummary | null;
    counts: StatusCounts | null; // NEW - server-computed status counts
    stats: ProcessingStats | null; // NEW - server-computed processing stats
    model: string | null; // AI model used for processing
    error: string | null;
    reconnecting: boolean;
    documentCount?: number;
    documentMetadata?: Record<string, DocumentMetadata>;
    questions: Map<string, QuestionState>;
  };
}

export const InfoHeader = ({ batchId, sseData }: InfoHeaderProps) => {
  const context = useContext(SurveyContext) as SurveyContextLoadedProps;
  const { surveyData } = context;

  // Destructure SSE data passed from parent - now includes server-provided counts and stats
  const { status, progress, summary, counts, stats, model, reconnecting, error, documentMetadata } = sseData;

  // Mutations for batch control
  const [pauseBatch] = usePauseBatchMutation();
  const [resumeBatch] = useResumeBatchMutation();
  const [abortBatch] = useAbortBatchMutation();

  const handlePause = async () => {
    pauseBatch({
      initiativeId: surveyData.initiativeId,
      surveyId: surveyData._id,
      batchId,
    })
      .unwrap()
      .then(() => {
        generateToast({ color: 'success', message: 'Processing has been paused. Click Resume to continue.' });
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Failed to pause processing. Please try again.';
        generateErrorToast({ message });
      });
  };

  const handleResume = async () => {
    resumeBatch({
      initiativeId: surveyData.initiativeId,
      surveyId: surveyData._id,
      batchId,
    })
      .unwrap()
      .then(() => {
        generateToast({ color: 'success', message: 'Batch resumed: Processing has been resumed.' });
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Failed to resume processing. Please try again.';
        generateErrorToast({ message });
      });
  };

  const handleStop = async () => {
    abortBatch({
      initiativeId: surveyData.initiativeId,
      surveyId: surveyData._id,
      batchId,
    })
      .unwrap()
      .then(() => {
        generateToast({ color: 'success', message: 'Batch stopped: Processing has been stopped.' });
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Failed to stop batch. Please try again.';
        generateErrorToast({ message });
      });
  };

  // Use server-provided counts and compute display stats
  const displayStats = useMemo(() => {
    const totalQuestions = progress?.total || 0;

    // Use server-provided counts (no client-side aggregation)
    const pending = counts?.pending ?? 0;
    const processing = counts?.processing ?? 0;
    const completed = counts?.completed ?? 0;
    const failed = counts?.failed ?? 0;

    const actualProcessed = completed + failed;
    const overallProgress =
      totalQuestions > 0 ? Math.min(100, Math.round((actualProcessed / totalQuestions) * 100)) : 0;

    return { totalQuestions, pending, processing, completed, failed, overallProgress };
  }, [counts, progress]);

  const { totalQuestions, pending, processing, completed, failed, overallProgress } = displayStats;

  // Extract document names from metadata
  const documentNames = useMemo(() => {
    if (!documentMetadata) return [];
    return Object.values(documentMetadata).map((doc) => doc.fileName);
  }, [documentMetadata]);

  // Processing stages from progress (updated via SSE progress events)
  // These track how many items were processed by each agent in the pipeline
  // Note: Stage counts can differ because questions may skip stages if they fail
  const getProgressPercentage = (count: number) => {
    return totalQuestions > 0 ? Math.min(100, Math.round((count / totalQuestions) * 100)) : 0;
  };
  const processingStages = [
    {
      name: 'Retrieved',
      progress: getProgressPercentage(progress?.retrieved || 0),
      completed: progress?.retrieved || 0,
    },
    {
      name: 'Answered',
      progress: getProgressPercentage(progress?.answered || 0),
      completed: progress?.answered || 0,
    },
    {
      name: 'Cited',
      progress: getProgressPercentage(progress?.cited || 0),
      completed: progress?.cited || 0,
    },
    {
      name: 'Validated',
      disabled: true,
      progress: getProgressPercentage(progress?.validated || 0),
      completed: progress?.validated || 0,
    },
    {
      name: 'Quality Checked',
      disabled: true,
      progress: getProgressPercentage(progress?.qualityChecked || 0),
      completed: progress?.qualityChecked || 0,
    },
    {
      name: 'Synthesized',
      disabled: true,
      progress: getProgressPercentage(progress?.synthesized || 0),
      completed: progress?.synthesized || 0,
    },
    {
      name: 'Reconciled',
      disabled: true,
      progress: getProgressPercentage(progress?.reconciled || 0),
      completed: progress?.reconciled || 0,
    },
  ];

  // Use server-provided time estimation
  const getEstimatedTimeRemaining = () => {
    // Use server-computed estimation if available
    if (stats?.estimatedTimeRemaining) {
      const totalSeconds = Math.round(stats.estimatedTimeRemaining);
      const minutes = Math.floor(totalSeconds / 60);
      return minutes > 0 ? `Est. ${minutes} min remaining` : 'Almost done';
    }
    return null;
  };

  const getLastUpdated = () => {
    if (!progress?.lastUpdate) return 'Waiting for updates...';
    const lastUpdate = new Date(progress.lastUpdate);
    return formatDateNonUTC(lastUpdate, DATE.HUMANIZE);
  };
  const getTotalTime = () => {
    if (!progress?.startTime || !progress?.lastUpdate) return 'Waiting for updates...';
    const diff = getDiff(progress.startTime, progress.lastUpdate);
    return getDuration(diff).humanize();
  };

  const getStatusColor = (): string => {
    if (status === 'processing') return 'primary';
    if (status === 'pausing') return 'warning';
    if (status === 'paused') return 'warning';
    if (status === 'completed') return 'success';
    return 'danger';
  };

  return (
    <DashboardSection className='batch-header' padding={1}>
      {/* Connection status banner */}
      {reconnecting && (
        <div className='alert alert-warning mb-3'>
          <i className='fal fa-spinner fa-spin me-2' />
          Reconnecting to live updates...
        </div>
      )}
      {error && (
        <div className='alert alert-danger mb-3'>
          <i className='fal fa-exclamation-triangle me-2' />
          {error}
        </div>
      )}

      <BasicAlert type='warning'>
        This component is NOT updating the questions at the moment, it is only informational.
      </BasicAlert>


      <div className='d-flex gap-2'>
        <span className='text-lg ms-auto'>
          <Badge color={getStatusColor()}>
            <i
              className={`fal ${status === 'processing' || status === 'pausing' ? 'fa-spinner fa-spin' : status === 'paused' ? 'fa-pause' : 'fa-check-circle'} me-1`}
            />
            {
              status === 'processing'
                ? 'Processing...'
                : status === 'pausing'
                  ? 'Pausing...'
                  : status === 'paused'
                    ? 'Paused'
                    : status === 'cancelled'
                      ? 'Cancelled'
                      : status === 'completed'
                        ? 'Completed'
                        : `Status: ${status}` // Shouldn't happen, but just in case
            }
          </Badge>
        </span>
        {status === 'processing' ? (
          <Button outline onClick={handlePause}>
            <i className='fal fa-pause me-2' />
            Pause
          </Button>
        ) : status === 'pausing' ? (
          <Button outline disabled>
            <i className='fal fa-pause me-2' />
            Pausing...
          </Button>
        ) : status === 'paused' ? (
          <Button outline onClick={handleResume}>
            <i className='fal fa-play me-2' />
            Resume
          </Button>
        ) : null}
        {(status === 'processing' || status === 'pausing' || status === 'paused') && (
          <Button outline onClick={handleStop} color='danger'>
            <i className='fal fa-stop me-2' />
            Abort
          </Button>
        )}
      </div>

      {/* Top Section - Title and Actions */}
      <div className='d-flex w-100 align-items-center justify-content-between mb-4'>
        <div className='d-flex w-100 align-items-center gap-4'>
          <div className='d-flex align-items-center gap-3'>
            <div className='p-2 rounded background-ThemeAccentExtralight'>
              <i className='fal fa-sparkles text-primary' style={{ fontSize: '1.25rem' }} />
            </div>
            <div>
              <h2 className='h5 text-dark mb-1'>{surveyData.name || 'Survey Auto-Answer'}</h2>
              <div className='d-flex flex-column gap-1'>
                <div className='d-flex flex-column small text-muted'>
                  <div><i className='fal fa-fw fa-clock me-1' /> {getLastUpdated()}</div>
                  <div><i className='fal fa-fw fa-clock-rotate-left me-1' /> {getTotalTime()}</div>
                  {documentNames.length > 0 && (
                    <div>
                      {documentNames.map((name, idx) => (
                        <span key={idx} className='text-truncate' style={{ maxWidth: '500px' }} title={name}>
                          <i className='fal fa-file-alt fa-fw me-2' />
                          {name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Compact Stats */}
          <div className='ms-auto d-flex align-items-center gap-3 ps-3'>
            {summary?.avgProcessingTime ? (
              <div className='d-flex align-items-center gap-2 ps-3 border-start'>
                <i className='fal fa-fw fa-clock text-muted' />
                <div className='d-flex flex-column'>
                  <span className='small text-muted'>Avg. Time</span>
                  <span className='small fw-bold text-dark'>{(summary.avgProcessingTime / 1000).toFixed(1)}s</span>
                </div>
              </div>
            ) : null}
            {summary?.avgQualityScore !== undefined && (
              <div className='d-flex align-items-center gap-2 ps-3 border-start'>
                <i className='fal fa-fw fa-chart-line text-success' />
                <div className='d-flex flex-column'>
                  <span className='small text-muted'>Avg. Confidence</span>
                  <span className='small fw-bold text-success'>{summary.avgQualityScore.toFixed(1)}%</span>
                </div>
              </div>
            )}
            {model && (
              <div className='d-flex align-items-center gap-2 ps-3 border-start'>
                <i className='fal fa-fw fa-robot text-muted' />
                <div className='d-flex flex-column'>
                  <span className='small text-muted'>Model</span>
                  <span className='small'>{getAIModelName(model)}</span>
                </div>
              </div>
            )}
            <div className='d-flex align-items-center gap-2 ps-3 border-start'>
              <i className='fal fa-fw fa-question-circle text-muted' />
              <div className='d-flex flex-column'>
                <span className='small text-muted'>Total Questions</span>
                <span className='small'>{totalQuestions}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='mb-4'>
        <div className='d-flex align-items-center justify-content-between small mb-2'>
          <span className='text-muted'>Overall Progress</span>
          <div className='d-flex align-items-center gap-3'>
            <span className='text-dark'>
              {completed} of {totalQuestions} questions
            </span>
            {getEstimatedTimeRemaining() && <span className='text-muted'>{getEstimatedTimeRemaining()}</span>}
          </div>
        </div>
        <Progress value={overallProgress} className='mb-2' style={{ height: '0.5rem' }} />
        <p className='small text-muted mb-0'>{overallProgress}% complete</p>
      </div>

      {/* Processing Stages */}
      <div className='mb-4'>
        <h3 className='small text-muted mb-3'>Processing Stages</h3>
        <div className='d-flex g-3 justify-content-between'>
          {processingStages.map((stage) => (
            <div key={stage.name} className='d-flex flex-column align-items-center flex-grow-1'>
              <div className='position-relative mb-2' style={{ width: '4rem', height: '4rem' }}>
                <ProgressCircle pc={stage.disabled ? 0 : stage.progress} disabled={stage.disabled ?? false} />
              </div>
              <span
                className={`small text-muted text-center mb-1 ${stage.disabled ? 'text-decoration-line-through' : ''}`}
              >
                {stage.name}
              </span>
              <div className='d-flex align-items-center gap-1'>
                <span className='small text-success'>{stage.completed}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Compact Summary Stats */}
      <div className='row g-2'>
        <div className='col-3'>
          <div className='p-2 rounded text-ThemeNeutralsDarkest background-ThemeNeutralsExtralight border border-ThemeNeutralsDark'>
            <div className='d-flex align-items-center gap-2 mb-1'>
              <i className='fal fa-clock' />
              <span className='small'>Pending</span>
            </div>
            <p className='h4 text-ThemeNeutralsDarkest mb-0'>{pending || 0}</p>
          </div>
        </div>

        <div className='col-3'>
          <div className='p-2 rounded text-ThemeAccentDark background-ThemeAccentExtralight border border-ThemeAccentMedium'>
            <div className='d-flex align-items-center gap-2 mb-1'>
              <i className={`fal fa-spinner ${processing > 0 ? 'fa-spin' : ''}`} />
              <span className='small'>Processing</span>
            </div>
            <p className='h4 text-ThemeAccentDark mb-0'>{processing}</p>
          </div>
        </div>

        <div className='col-3'>
          <div className='p-2 rounded text-ThemeSuccessDark background-ThemeSuccessExtralight border border-ThemeSuccessMedium'>
            <div className='d-flex align-items-center gap-2 mb-1 text-ThemeSuccessMedium'>
              <i className='fal fa-check-circle' />
              <span className='small'>Completed</span>
            </div>
            <p className='h4 text-ThemeSuccessDark mb-0'>{completed}</p>
          </div>
        </div>

        <div className='col-3'>
          <div className='p-2 rounded text-ThemeDangerDark background-ThemeDangerExtralight border border-ThemeDangerMedium'>
            <div className='d-flex align-items-center gap-2 mb-1'>
              <i className='fal fa-exclamation-triangle' />
              <span className='small'>Failed</span>
            </div>
            <p className='h4 text-ThemeDangerDark mb-0'>{failed}</p>
          </div>
        </div>
      </div>
    </DashboardSection>
  );
};

function ProgressCircle({ pc, disabled }: { pc: number; disabled: boolean }) {
  return (
    <>
      <svg style={{ width: '4rem', height: '4rem', transform: 'rotate(-90deg)' }}>
        <circle
          cx='32'
          cy='32'
          r='28'
          stroke='currentColor'
          strokeWidth='8'
          fill='none'
          className='opacity-50'
        />
        <circle
          cx='32'
          cy='32'
          r='28'
          stroke='currentColor'
          strokeWidth='6'
          fill='none'
          strokeDasharray={`${2 * Math.PI * 28}`}
          strokeDashoffset={`${2 * Math.PI * 28 * (1 - pc / 100)}`}
          className={disabled ? 'opacity-50' : 'text-primary'}
          strokeLinecap='round'
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>
      <div className='position-absolute top-50 start-50 translate-middle'>
        <span className='small fw-bold'>{pc}%</span>
      </div>
    </>
  );
}
